import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import api from "../services/api";
import refreshToken from "../utils/refreshToken";
import { useToast } from "./toast";
import type { PhotoAiPreviewItem, PhotoThumb } from "../types/photoAiEdit";
import {
  applyAiPreview,
  discardAiPreview,
  logPhotoAiPreviewGenerated,
  mapPreviewResponse,
  parseAiPreviewError,
  requestCustomEditPreview,
  requestVirtualStagingPreview,
} from "../services/photoAiEdit";
import {
  sanitizePhotoAiPrompt,
  validatePhotoAiPrompt,
} from "../utils/photoAiPrompt";

type UsePhotoAiEditOptions = {
  accessToken: string;
  dateToExpires: number;
  photos: PhotoThumb[];
  onPhotoApplied: (newPhoto: Record<string, unknown>) => void;
  onPreviewGenerated?: () => void;
};

export function usePhotoAiEdit({
  accessToken,
  dateToExpires,
  photos,
  onPhotoApplied,
  onPreviewGenerated,
}: UsePhotoAiEditOptions) {
  const { addToast } = useToast();

  const [aiPreviews, setAiPreviews] = useState<PhotoAiPreviewItem[]>([]);
  const [generatingAI, setGeneratingAI] = useState<Record<number, boolean>>({});
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [promptModalPhotoId, setPromptModalPhotoId] = useState<number | null>(
    null
  );
  const [promptDraft, setPromptDraft] = useState("");
  const abortControllersRef = useRef<Record<number, AbortController>>({});

  useEffect(() => {
    if (aiPreviews.length === 0 && isPreviewModalOpen) {
      setIsPreviewModalOpen(false);
    }
  }, [aiPreviews.length, isPreviewModalOpen]);

  const authorizeApi = useCallback(async () => {
    const token = await refreshToken(dateToExpires, accessToken);
    api.defaults.headers.authorization = `Bearer ${token || accessToken}`;
  }, [accessToken, dateToExpires]);

  const getOriginalUrl = useCallback(
    (photoId: number) => {
      const photo = photos.find((p) => p.id === photoId);
      return photo?.url_m || photo?.url_l || "";
    },
    [photos]
  );

  const openCustomEditPrompt = useCallback(
    (photoId: number, initialPrompt = "") => {
      setPromptModalPhotoId(photoId);
      setPromptDraft(initialPrompt);
    },
    []
  );

  const closeCustomEditPrompt = useCallback(() => {
    const photoId = promptModalPhotoId;
    if (photoId && generatingAI[photoId]) {
      abortControllersRef.current[photoId]?.abort();
    }
    setPromptModalPhotoId(null);
    setPromptDraft("");
  }, [generatingAI, promptModalPhotoId]);

  const cancelGeneration = useCallback((photoId: number) => {
    abortControllersRef.current[photoId]?.abort();
  }, []);

  const submitCustomEdit = useCallback(async () => {
    const validationError = validatePhotoAiPrompt(promptDraft);
    if (validationError) {
      addToast({ title: validationError, type: "error" });
      return;
    }

    const photoId = promptModalPhotoId;
    if (!photoId || generatingAI[photoId]) return;

    const prompt = sanitizePhotoAiPrompt(promptDraft);
    const controller = new AbortController();
    abortControllersRef.current[photoId] = controller;

    setGeneratingAI((prev) => ({ ...prev, [photoId]: true }));

    try {
      await authorizeApi();
      const response = await requestCustomEditPreview(
        api,
        photoId,
        prompt,
        controller.signal
      );

      const preview = mapPreviewResponse(
        response.data.data,
        photoId,
        "custom_edit",
        getOriginalUrl(photoId)
      );

      setAiPreviews((prev) => [...prev, preview]);
      setPromptModalPhotoId(null);
      setPromptDraft("");
      setIsPreviewModalOpen(true);
      onPreviewGenerated?.();

      logPhotoAiPreviewGenerated({
        photoId,
        previewId: preview.id,
        prompt: preview.user_prompt,
        improvementSummary: preview.improvement_summary,
        fromCache: preview.from_cache,
      });

      addToast({
        title: preview.from_cache
          ? "Preview recuperado do cache."
          : "Preview de edição com IA gerado!",
        type: "success",
      });
    } catch (err) {
      const { message, retryable } = parseAiPreviewError(err);
      if (!axios.isCancel(err)) {
        addToast({
          title: message,
          type: "error",
          description: retryable ? "Você pode ajustar o prompt e tentar novamente." : undefined,
        });
      } else {
        addToast({ title: message, type: "info" });
      }
    } finally {
      setGeneratingAI((prev) => ({ ...prev, [photoId]: false }));
      delete abortControllersRef.current[photoId];
    }
  }, [
    addToast,
    authorizeApi,
    generatingAI,
    getOriginalUrl,
    onPreviewGenerated,
    promptDraft,
    promptModalPhotoId,
  ]);

  const handleGenerateVirtualStaging = useCallback(
    async (photoId: number) => {
      if (generatingAI[photoId]) return;

      const controller = new AbortController();
      abortControllersRef.current[photoId] = controller;
      setGeneratingAI((prev) => ({ ...prev, [photoId]: true }));

      try {
        await authorizeApi();
        const response = await requestVirtualStagingPreview(
          api,
          photoId,
          controller.signal
        );

        const preview = mapPreviewResponse(
          response.data.data,
          photoId,
          "virtual_staging",
          getOriginalUrl(photoId)
        );

        setAiPreviews((prev) => [...prev, preview]);
        setIsPreviewModalOpen(true);
        onPreviewGenerated?.();

        logPhotoAiPreviewGenerated({
          photoId,
          previewId: preview.id,
          fromCache: preview.from_cache,
        });

        addToast({
          title: "Preview de mobília virtual gerado!",
          type: "success",
        });
      } catch (err) {
        const { message, retryable } = parseAiPreviewError(err);
        if (!axios.isCancel(err)) {
          addToast({
            title: message,
            type: "error",
            description: retryable ? "Tente novamente em instantes." : undefined,
          });
        } else {
          addToast({ title: message, type: "info" });
        }
      } finally {
        setGeneratingAI((prev) => ({ ...prev, [photoId]: false }));
        delete abortControllersRef.current[photoId];
      }
    },
    [addToast, authorizeApi, generatingAI, getOriginalUrl, onPreviewGenerated]
  );

  const handleApplyAI = useCallback(
    async (preview: PhotoAiPreviewItem) => {
      try {
        await authorizeApi();
        const response = await applyAiPreview(api, preview.photo_id, preview.id);

        if (response.data.success) {
          onPhotoApplied(response.data.data);
          setAiPreviews((prev) => prev.filter((p) => p.id !== preview.id));
          addToast({
            title: "IA aplicada! Nova foto adicionada à galeria.",
            type: "success",
          });
        }
      } catch (err) {
        console.error(err);
        addToast({ title: "Erro ao aplicar IA", type: "error" });
      }
    },
    [addToast, authorizeApi, onPhotoApplied]
  );

  const handleDiscardAI = useCallback(
    async (previewId: number) => {
      try {
        await authorizeApi();
        await discardAiPreview(api, previewId);
        setAiPreviews((prev) => prev.filter((p) => p.id !== previewId));
        addToast({ title: "Preview descartado", type: "success" });
      } catch (err) {
        console.error(err);
        addToast({ title: "Erro ao descartar preview", type: "error" });
      }
    },
    [addToast, authorizeApi]
  );

  const handleTryAgainAI = useCallback(
    async (preview: PhotoAiPreviewItem) => {
      const previousPrompt = preview.user_prompt ?? "";
      await handleDiscardAI(preview.id);
      openCustomEditPrompt(preview.photo_id, previousPrompt);
    },
    [handleDiscardAI, openCustomEditPrompt]
  );

  const isPromptModalOpen = promptModalPhotoId !== null;
  const isPromptModalLoading =
    promptModalPhotoId !== null && generatingAI[promptModalPhotoId] === true;
  const promptModalPhotoUrl = promptModalPhotoId
    ? getOriginalUrl(promptModalPhotoId)
    : "";

  return {
    aiPreviews,
    generatingAI,
    isPreviewModalOpen,
    setIsPreviewModalOpen,
    isPromptModalOpen,
    isPromptModalLoading,
    promptModalPhotoId,
    promptModalPhotoUrl,
    promptDraft,
    setPromptDraft,
    openCustomEditPrompt,
    closeCustomEditPrompt,
    cancelGeneration,
    submitCustomEdit,
    handleGenerateVirtualStaging,
    handleApplyAI,
    handleDiscardAI,
    handleTryAgainAI,
  };
}
